import { Controller } from 'stimulus'
import trackBusy from '../lib/metrics/busy'
import {
  controlChart, fieldStrip, histogram, binsHistogram, weeklyWhiskers,
  kpiTile, renderTable, fmtNum, groupFieldStrip, groupTrend, SLOTS
} from '../lib/metrics/charts'
import { renderTraitChips, describeTraitFilters } from '../lib/metrics/trait_chips'
import {
  createSearchList, fieldChips, renderFilterChips, stateFromTokens, stateToTokens,
  isEmptyState, matchesTerm, normalizeTerm, CONTAINS_PREFIX
} from '../lib/metrics/search_list'
import renderEmptyNote, { rangePhrase } from '../lib/metrics/empty_note'

const GROUP_NAMES = ['Group A', 'Group B', 'Group C']

// Drives the Trends tab. Task-first: picking task names or an "All
// containing ..." term in the Task search list loads a Results-Summary
// style strip for EVERY numeric field on those lineages; same-named
// measurements combine across the matched tasks, names left out (unchecked
// rows) stay out. One dot is one sample's average across jobs, red when
// any of its results failed (stored status), pale under 3 results, a
// diamond when it's a statistical outlier. Clicking a field row drills into
// the control chart / distribution / failed-results cards below.
//
// Filters (lib/metrics/search_list): task[] (~terms and exact names) +
// exclude_task[]; per trait traits[] (<name>:~<words> or <name>:<value>) +
// exclude_traits[]. Same params and chips as the Tasks & Results tab.
//
// Compare mode: up to 3 groups, each a set of exact trait values ANDed
// onto the shared range + task + trait filters (group_traits[]). Strips
// draw every group's dots on one axis per measurement; the drill-in becomes
// one median line per group.
export default class extends Controller {
  static targets = [
    'range', 'taskSearch', 'traitPicker', 'valueSearch', 'chips', 'emptyNote',
    'strips', 'stripsSub',
    'kpiN', 'kpiMean', 'kpiSd', 'kpiPass',
    'chartSub', 'chart', 'chartTable', 'hist', 'histSub', 'failed',
    'crit', 'critSub', 'jobs', 'jobsSub',
    'compareBtn', 'groupRows', 'singleCard', 'compareCard',
    'cmpTiles', 'cmpTitle', 'cmpSub', 'cmpLegend', 'cmpChart'
  ];

  connect() {
    // trait value filters by trait key (normalized name), in the order
    // added: { traitName, terms, picks, excludes, termTotals }
    this.traitStates = new Map()
    this.groups = []
    // search lists inside the compare group rows (destroyed on re-render)
    this.groupLists = []
    this.field = null
    // display labels by normalized task key, and names per task term
    // ("water burst · 23 of 24 names"), shared with the Task search list
    this.taskLabels = new Map()
    this.taskTermTotals = new Map()
    this.taskList = createSearchList(this.taskSearchTarget, {
      label: 'Task',
      placeholder: 'Pick a task',
      searchPlaceholder: 'Search tasks',
      nounOne: 'name',
      nounMany: 'names',
      // trends/tasks counts only results that recorded a numeric value
      countNoun: 'results with measurements',
      outOfRangeText: 'no measurements in range',
      labels: this.taskLabels,
      termTotals: this.taskTermTotals,
      fetchOptions: (q, state) => this.fetchTaskOptions(q, state),
      onApply: () => {
        this.renderChips()
        this.load()
      },
      onLabels: () => {
        this.renderChips()
        this.renderStripsSub()
      }
    })
    this.renderValueList(false)
    this.initFromUrl()
    // restored picks and exclusions need display names
    this.taskList.ensureLabels()
    this.onShow = () => this.load()
    window.addEventListener('metrics:trends-shown', this.onShow)
    // Direct ?tab=trends navigation (saved views, shared URLs): the outer
    // metrics controller activates this panel during ITS connect(), which
    // runs before this listener registers, so the shown event is already
    // gone. The active class survives, so load from it.
    if (this.element.classList.contains('active')) this.load()
    this.resizeHandler = () => {
      clearTimeout(this.resizeTimer)
      this.resizeTimer = setTimeout(() => {
        if (this.element.offsetParent !== null) this.load()
      }, 160)
    }
    window.addEventListener('resize', this.resizeHandler)
  }

  disconnect() {
    window.removeEventListener('metrics:trends-shown', this.onShow)
    window.removeEventListener('resize', this.resizeHandler)
    // Turbolinks caches the page: drop the popovers and their document
    // listeners (connect() rebuilds the controls from the anchors)
    this.taskList.destroy()
    if (this.valueList) this.valueList.destroy()
    this.destroyGroupLists()
  }

  // Old saved views carry a scalar task= and exact trait values; new ones
  // task[] (~terms too), exclude_task[], traits[] with ~terms and
  // exclude_traits[]. All restore.
  initFromUrl() {
    const url = new URLSearchParams(window.location.search)
    if (url.get('tab') !== 'trends') return
    if (url.get('range')) this.rangeTarget.value = url.get('range')
    // exact names are normalized keys (the server lower-cases them too)
    const taskKey = t => (String(t).startsWith(CONTAINS_PREFIX) ? t : String(t).trim().toLowerCase())
    const tasks = url.getAll('task[]').concat(url.getAll('task')).map(taskKey)
    const excluded = url.getAll('exclude_task[]').concat(url.getAll('exclude_task')).map(taskKey)
    if (tasks.length || excluded.length) this.taskList.setState(stateFromTokens(tasks, excluded))
    if (tasks.length) this.pendingField = url.get('field')
    const eachTrait = (param, fn) => url.getAll(param).forEach((raw) => {
      const [rawKey, value] = String(raw).split(/:(.*)/s)
      const traitId = decodeURIComponent(rawKey || '')
      if (traitId && value) fn(this.traitState(traitId), value)
    })
    eachTrait('traits[]', (st, value) => {
      if (value.startsWith(CONTAINS_PREFIX)) st.terms.push(normalizeTerm(value.slice(CONTAINS_PREFIX.length)))
      else st.picks.push(value)
    })
    eachTrait('exclude_traits[]', (st, value) => st.excludes.push(value))
    this.renderChips()
    const rawGroups = url.getAll('groups[]')
    if (rawGroups.length >= 2) {
      this.groups = rawGroups.slice(0, 3).map((raw, slot) => ({
        slot,
        filters: raw.split('|').map(pair => this.parseTrait(pair)).filter(Boolean)
      }))
      this.enterCompare()
    }
  }

  // "<url-encoded trait key>:<value>" -> { traitId, traitName, value }
  parseTrait(raw) {
    const [rawKey, value] = String(raw).split(/:(.*)/s)
    const traitId = decodeURIComponent(rawKey || '')
    if (!traitId || !value) return null
    return { traitId, traitName: this.traitName(traitId), value }
  }

  traitName(traitId) {
    const opt = [...this.traitPickerTarget.options].find(o => o.value === traitId)
    return opt ? opt.textContent : traitId
  }

  traitParam(f) {
    return `${encodeURIComponent(f.traitId)}:${f.value}`
  }

  // Captured into saved views (page_name metrics#show). Arrays serialize
  // as task[]= / exclude_task[]= / traits[]= / exclude_traits[]= / groups[]=.
  viewParams() {
    const params = { tab: 'trends', range: this.rangeTarget.value }
    const state = this.taskList.getState()
    const tasks = stateToTokens(state)
    if (tasks.length) params.task = tasks
    if (state.excludes.length) params.exclude_task = state.excludes
    if (this.field) params.field = this.field
    const traits = new URLSearchParams()
    this.appendTraitParams(traits)
    if (traits.getAll('traits[]').length) params.traits = traits.getAll('traits[]')
    if (traits.getAll('exclude_traits[]').length) params.exclude_traits = traits.getAll('exclude_traits[]')
    if (this.compareActive()) {
      params.groups = this.groups.map(g => g.filters.map(f => this.traitParam(f)).join('|'))
    }
    return params
  }

  busyEl() { return this.element }

  // Guarded responses belonging to a superseded load() are dropped, so a
  // slow older request can't paint over a newer one.
  fetchJson(url, onData, guarded = true) {
    const seq = this.loadSeq || 0
    const req = fetch(url, { headers: { Accept: 'application/json' } })
    if (guarded) trackBusy(this.busyEl(), req)
    req
      .then(r => (r.ok ? r.json() : Promise.reject(new Error(r.status))))
      .then((data) => {
        if (guarded && seq !== (this.loadSeq || 0)) return
        onData(data)
      })
      .catch(() => {})
  }

  // One fetch per compare group, all tracked as one busy span; resolves
  // with the payloads in group order, or never when superseded.
  fetchPerGroup(urlFor) {
    const seq = this.loadSeq || 0
    const all = Promise.all(this.groups.map(group => fetch(urlFor(group), { headers: { Accept: 'application/json' } })
      .then(r => (r.ok ? r.json() : Promise.reject(new Error(r.status))))))
    return new Promise((resolve) => {
      trackBusy(this.busyEl(), all).then((results) => {
        if (seq === (this.loadSeq || 0)) resolve(results)
      }).catch(() => {})
    })
  }

  optionsFetch(url, p) {
    return fetch(`${url}?${p.toString()}`, { headers: { Accept: 'application/json' } })
      .then(r => (r.ok ? r.json() : Promise.reject(new Error(r.status))))
  }

  // ---- task selection ----

  // task[] tokens: ~terms and exact names. Trends needs at least one;
  // exclusions alone don't pick anything to trend.
  taskValues() {
    return stateToTokens(this.taskList.getState())
  }

  hasTask() {
    return this.taskValues().length > 0
  }

  // Task search list rows: names with numeric values in range, counted
  // (results with measurements) under the trait filters, not the task
  // filter itself.
  fetchTaskOptions(q, state) {
    const p = new URLSearchParams({ range: this.rangeTarget.value, list: '1' })
    if (q) p.set('q', q)
    state.picks.concat(state.excludes).forEach(v => p.append('selected[]', v))
    this.appendTraitParams(p)
    return this.optionsFetch(this.data.get('tasksUrl'), p)
  }

  // Trait value rows (results/trait_values, shared with Tasks & Results):
  // counted under the task filter and the other traits, not this trait's.
  fetchTraitValueOptions(traitId, q, state) {
    const p = new URLSearchParams({ trait: traitId, range: this.rangeTarget.value, list: '1' })
    if (q) p.set('q', q)
    state.picks.concat(state.excludes).forEach(v => p.append('selected[]', v))
    this.appendTaskParams(p)
    this.appendTraitParams(p, traitId)
    return this.optionsFetch(this.data.get('traitValuesUrl'), p)
  }

  // "water burst · 23 of 24 names", "Water Burst Test or Tensile Test",
  // "3 names · 1 left out". Name counts come from the Task search list
  // (numeric-measurement names in range under the trait filters).
  taskSummary() {
    const s = this.taskList.getState()
    const labelFor = v => this.taskList.labelFor(v)
    const names = n => `${fmtNum(n)} name${n === 1 ? '' : 's'}`
    const under = t => s.excludes.filter(v => matchesTerm(v, t) || matchesTerm(labelFor(v), t)).length
    const parts = s.terms.map((t) => {
      const total = this.taskTermTotals.get(t)
      if (total === undefined) return t
      const out = under(t)
      return out ? `${t} · ${fmtNum(Math.max(0, total - out))} of ${names(total)}` : `${t} · ${names(total)}`
    })
    if (s.picks.length) parts.push(s.picks.length <= 2 ? s.picks.map(labelFor).join(' or ') : names(s.picks.length))
    const loose = s.excludes.filter(v => !s.terms.some(t => matchesTerm(v, t) || matchesTerm(labelFor(v), t))).length
    let text = parts.join(' + ')
    if (loose) text += ` · ${fmtNum(loose)} left out`
    return text
  }

  // Strips subtitle: the task summary (it can change when term counts
  // arrive) plus the tail the last render chose.
  renderStripsSub() {
    if (!this.stripsSubTail || !this.hasTask()) return
    this.stripsSubTarget.textContent = `${this.taskSummary()} · ${this.stripsSubTail}`
  }

  setStripsSub(tail) {
    this.stripsSubTail = tail
    this.renderStripsSub()
  }

  // "Nothing in this range" note over the cards, for a picked task
  // selection whose strips came back empty. Hidden otherwise.
  renderRangeNote(data) {
    if (!data || this.compareActive() || data.fields.length) {
      renderEmptyNote(this.emptyNoteTarget, null)
      return
    }
    const range = this.rangeTarget.value
    renderEmptyNote(this.emptyNoteTarget, {
      noun: 'results with numeric measurements',
      rangeLabel: rangePhrase(range),
      latest: data.latest_result_at || null,
      onWiden: range === '365' ? null : () => {
        this.rangeTarget.value = '365'
        this.load()
      }
    })
  }

  // task[] plus exclude_task[]
  appendTaskParams(p) {
    const state = this.taskList.getState()
    stateToTokens(state).forEach(t => p.append('task[]', t))
    state.excludes.forEach(t => p.append('exclude_task[]', t))
  }

  // traits[] (picks and ~terms) and exclude_traits[]; except: a trait key
  // left out (its own value list counts without its own filter)
  appendTraitParams(p, except = null) {
    this.traitStates.forEach((st, traitId) => {
      if (traitId === except) return
      const k = encodeURIComponent(traitId)
      st.terms.forEach(t => p.append('traits[]', `${k}:${CONTAINS_PREFIX}${t}`))
      st.picks.forEach(v => p.append('traits[]', `${k}:${v}`))
      st.excludes.forEach(v => p.append('exclude_traits[]', `${k}:${v}`))
    })
  }

  // Shared params: range + task[] / exclude_task[] + shared traits[] /
  // exclude_traits[]. A compare group's values go as group_traits[], a
  // separate AND layer: sent as traits[] they would OR into the shared
  // values of the same trait and widen the group instead of narrowing it.
  baseParams(group = null) {
    const p = new URLSearchParams({ range: this.rangeTarget.value })
    this.appendTaskParams(p)
    this.appendTraitParams(p)
    if (group) group.filters.forEach(f => p.append('group_traits[]', this.traitParam(f)))
    return p
  }

  // Name/value counts behind the term chips and the strips subtitle
  // ("water burst · 23 of 24 names"), under the current filters. Served
  // from the server's cached option rows.
  refreshTermCounts() {
    this.taskList.forgetCounts()
    this.taskList.ensureCounts()
    this.traitStates.forEach((st, traitId) => {
      st.termTotals.clear()
      if (!st.terms.length) return
      Promise.all(st.terms.map(t => this.fetchTraitValueOptions(traitId, t, st)
        .then((data) => { st.termTotals.set(t, data.total_options || 0) })))
        .then(() => this.renderChips())
        .catch(() => {})
    })
  }

  // ---- trait value filters: pick a trait, then its value search list ----

  traitState(traitId) {
    let st = this.traitStates.get(traitId)
    if (!st) {
      st = {
        traitName: this.traitName(traitId), terms: [], picks: [], excludes: [], termTotals: new Map()
      }
      this.traitStates.set(traitId, st)
    }
    return st
  }

  // Trait picker change: show the chosen trait's value list, open.
  traitChosen() {
    this.renderValueList(true)
  }

  renderValueList(open) {
    if (this.valueList) this.valueList.destroy()
    const traitId = this.traitPickerTarget.value
    if (!traitId) {
      this.valueList = createSearchList(this.valueSearchTarget, {
        label: 'Trait value', fetchOptions: () => Promise.resolve({ options: [] })
      })
      this.valueList.setDisabled(true, 'Pick a trait first')
      return
    }
    const existing = this.traitStates.get(traitId)
    const traitName = existing ? existing.traitName : this.traitPickerTarget.selectedOptions[0].textContent
    this.valueList = createSearchList(this.valueSearchTarget, {
      label: traitName,
      placeholder: 'Any value',
      searchPlaceholder: `Search ${traitName}`,
      nounOne: 'value',
      nounMany: 'values',
      countNoun: 'results',
      state: existing,
      termTotals: existing ? existing.termTotals : undefined,
      fetchOptions: (q, state) => this.fetchTraitValueOptions(traitId, q, state),
      onApply: state => this.setTraitState(traitId, state)
    })
    if (open) this.valueList.open()
  }

  // Chip body click: bring that trait's list up.
  openTrait(traitId) {
    this.traitPickerTarget.value = traitId
    this.renderValueList(true)
  }

  setTraitState(traitId, state) {
    const st = this.traitState(traitId)
    if (!st.termTotals.size && this.valueList && this.traitPickerTarget.value === traitId) {
      this.valueList.termTotals.forEach((n, t) => st.termTotals.set(t, n))
    }
    Object.assign(st, { terms: state.terms, picks: state.picks, excludes: state.excludes })
    if (isEmptyState(st)) this.traitStates.delete(traitId)
    if (this.valueList && this.traitPickerTarget.value === traitId) this.valueList.setState(st)
    this.renderChips()
    this.load()
  }

  // Task chips then one set per trait (terms, picks, red "not ..." chips);
  // a chip's body reopens its list.
  renderChips() {
    const host = this.chipsTarget
    host.innerHTML = ''
    host.classList.add('m-sl-chips')
    renderFilterChips(host, this.taskList.chips())
    this.traitStates.forEach((st, traitId) => {
      renderFilterChips(host, fieldChips(st, {
        label: st.traitName,
        nounOne: 'value',
        nounMany: 'values',
        termTotals: st.termTotals,
        onChange: state => this.setTraitState(traitId, state),
        onOpen: () => this.openTrait(traitId)
      }))
    })
  }

  chipFilters(filters) {
    return filters.map(f => ({ traitId: f.traitId, label: f.traitName, value: f.value }))
  }

  // ---- compare groups ----

  compareActive() {
    return this.groups.length >= 2
  }

  // A shared trait holding 2 or 3 picked values seeds one group per value
  // (the "3 part numbers, compare them" path); otherwise two empty groups.
  startCompare() {
    const multi = [...this.traitStates.entries()].filter(([, st]) => st.picks.length >= 2)
    if (multi.length === 1) {
      const [traitId, st] = multi[0]
      this.groups = st.picks.slice(0, 3).map((value, slot) => ({
        slot, filters: [{ traitId, traitName: st.traitName, value }]
      }))
    } else {
      this.groups = [{ slot: 0, filters: [] }, { slot: 1, filters: [] }]
    }
    this.enterCompare()
    this.load()
  }

  enterCompare() {
    this.renderGroupRows()
    this.singleCardTargets.forEach(el => Object.assign(el.style, { display: 'none' }))
    this.compareCardTargets.forEach(el => Object.assign(el.style, { display: '' }))
    this.compareBtnTarget.style.display = 'none'
  }

  exitCompare() {
    this.destroyGroupLists()
    this.groups = []
    this.groupRowsTarget.innerHTML = ''
    this.singleCardTargets.forEach(el => Object.assign(el.style, { display: '' }))
    this.compareCardTargets.forEach(el => Object.assign(el.style, { display: 'none' }))
    this.compareBtnTarget.style.display = ''
    this.load()
  }

  groupLabel(group) {
    if (!group.filters.length) return `${GROUP_NAMES[group.slot]}: all matching`
    if (group.filters.length === 1 || group.filters.every(f => f.traitId === group.filters[0].traitId)) {
      return group.filters.map(f => f.value).join(' or ')
    }
    return describeTraitFilters(this.chipFilters(group.filters))
  }

  destroyGroupLists() {
    (this.groupLists || []).forEach(list => list.destroy())
    this.groupLists = []
  }

  // A group row's value picker: a search list of the chosen trait's values
  // with exact picks only (no terms or exclusions in groups). Applying
  // replaces the group's values of that trait.
  mountGroupList(group, anchor, traitId, open) {
    const old = this.groupLists.find(l => l.anchor === anchor)
    if (old) {
      old.destroy()
      this.groupLists = this.groupLists.filter(l => l !== old)
    }
    let list
    if (!traitId) {
      list = createSearchList(anchor, { label: 'Group value', fetchOptions: () => Promise.resolve({ options: [] }) })
      list.setDisabled(true, 'Pick a trait')
    } else {
      const traitName = this.traitName(traitId)
      list = createSearchList(anchor, {
        label: `${GROUP_NAMES[group.slot]}: ${traitName}`,
        placeholder: 'Pick values',
        searchPlaceholder: `Search ${traitName}`,
        nounOne: 'value',
        nounMany: 'values',
        countNoun: 'results',
        supportsTerms: false,
        state: { terms: [], picks: group.filters.filter(f => f.traitId === traitId).map(f => f.value), excludes: [] },
        fetchOptions: (q, state) => this.fetchTraitValueOptions(traitId, q, state),
        onApply: (state) => {
          Object.assign(group, {
            filters: group.filters.filter(f => f.traitId !== traitId)
              .concat(state.picks.map(value => ({ traitId, traitName, value })))
          })
          this.renderGroupRows()
          this.load()
        }
      })
    }
    this.groupLists.push(list)
    if (open) list.open()
  }

  renderGroupRows() {
    this.destroyGroupLists()
    const host = this.groupRowsTarget
    host.innerHTML = ''
    this.groups.forEach((group, gi) => {
      const row = document.createElement('div')
      row.className = 'm-grp-row'
      const dot = document.createElement('span')
      dot.className = 'm-gdot'
      dot.style.background = SLOTS[group.slot]
      row.appendChild(dot)
      const name = document.createElement('span')
      name.className = 'm-gname'
      name.textContent = GROUP_NAMES[group.slot]
      row.appendChild(name)

      renderTraitChips(row, this.chipFilters(group.filters), (_filter, index) => {
        group.filters.splice(index, 1)
        this.renderGroupRows()
        this.load()
      })

      // the trait last used in this group stays chosen, so its picker
      // shows the group's values of it ("2 values") and adds to them
      const traitSel = document.createElement('select')
      traitSel.setAttribute('aria-label', `${GROUP_NAMES[group.slot]} trait`)
      traitSel.appendChild(new Option('+ Add trait…', ''));
      [...this.traitPickerTarget.options].slice(1).forEach((o) => {
        traitSel.appendChild(new Option(o.textContent, o.value))
      })
      const lastTrait = group.pickerTrait || (group.filters.length ? group.filters[group.filters.length - 1].traitId : '')
      if ([...traitSel.options].some(o => o.value === lastTrait)) traitSel.value = lastTrait
      const anchor = document.createElement('div')
      anchor.className = 'm-grp-values'
      row.appendChild(traitSel)
      row.appendChild(anchor)
      this.mountGroupList(group, anchor, traitSel.value, false)
      traitSel.addEventListener('change', () => {
        Object.assign(group, { pickerTrait: traitSel.value })
        this.mountGroupList(group, anchor, traitSel.value, !!traitSel.value)
      })

      if (this.groups.length > 2) {
        const rm = document.createElement('button')
        rm.type = 'button'
        rm.className = 'm-btn'
        rm.textContent = '✕ Remove group'
        rm.addEventListener('click', () => {
          this.groups.splice(gi, 1)
          this.groups.forEach((g, i) => { Object.assign(g, { slot: i }) })
          this.renderGroupRows()
          this.load()
        })
        row.appendChild(rm)
      }
      host.appendChild(row)
    })

    const actions = document.createElement('div')
    actions.className = 'm-grp-actions'
    if (this.groups.length < 3) {
      const add = document.createElement('button')
      add.type = 'button'
      add.className = 'm-btn'
      add.textContent = '+ Add comparison group'
      add.addEventListener('click', () => {
        this.groups.push({ slot: this.groups.length, filters: [] })
        this.renderGroupRows()
        this.load()
      })
      actions.appendChild(add)
    }
    const exit = document.createElement('button')
    exit.type = 'button'
    exit.className = 'm-btn'
    exit.textContent = 'Exit compare'
    exit.addEventListener('click', () => this.exitCompare())
    actions.appendChild(exit)
    host.appendChild(actions)
  }

  // ---- load & render ----

  load() {
    this.loadSeq = (this.loadSeq || 0) + 1
    // "23 of 24" counts are faceted like the lists: recount the terms
    // whenever the filters change
    const filterKey = this.baseParams().toString()
    if (filterKey !== this.lastFilterKey) {
      this.lastFilterKey = filterKey
      this.refreshTermCounts()
    }
    if (!this.hasTask() || this.compareActive()) renderEmptyNote(this.emptyNoteTarget, null)
    if (!this.hasTask()) {
      this.stripsSubTail = null
      this.stripsTarget.innerHTML = ''
      this.stripsSubTarget.textContent = 'Pick a task. Every numeric result measurement on it renders as a strip of samples'
      this.renderEmpty('Pick a task above')
      return
    }
    if (this.compareActive()) {
      this.fetchPerGroup(group => `${this.data.get('stripsUrl')}?${this.baseParams(group).toString()}`)
        .then(results => this.renderCompareStrips(results))
      return
    }
    this.fetchJson(`${this.data.get('stripsUrl')}?${this.baseParams().toString()}`, data => this.renderStrips(data))
  }

  // Picks the drill-in field from the strips' field names: a restored
  // ?field= first, then the current pick, else the top row.
  resolveField(names) {
    if (this.pendingField && names.includes(this.pendingField)) this.field = this.pendingField
    this.pendingField = null
    if (!this.field || !names.includes(this.field)) [this.field] = names
  }

  stripLegendText() {
    return 'one dot = one sample\'s average across jobs · red = has a failed result · pale = under 3 results · ◆ = outlier · click a measurement to open its trend'
  }

  renderStrips(data) {
    this.renderRangeNote(data)
    const host = this.stripsTarget
    host.innerHTML = ''
    this.stripFields = data.fields.map(f => f.name)
    if (!data.fields.length) {
      this.setStripsSub('no numeric result measurements in range')
      this.renderEmpty('No numeric result measurements to trend on this task')
      return
    }
    this.resolveField(this.stripFields)
    this.setStripsSub(this.stripLegendText())

    const wrap = document.createElement('div')
    wrap.className = 'm-fstrips'
    // attach BEFORE drawing: each strip SVG sizes itself from clientWidth,
    // and a detached container measures 0, so every strip would fall back
    // to 480px
    host.appendChild(wrap)
    data.fields.forEach((f) => {
      const outliers = this.outlierIds(f.points)
      const points = f.points.map(p => ({ ...p, outlier: outliers.has(p.sample_id) }))
      const judged = f.criteria_state !== 'none'
      const avgs = points.map(p => p.avg)
      const dp = avgs.some(v => !Number.isInteger(v)) ? 1 : 0

      const bits = [`${fmtNum(f.samples_total)} samples`, `${fmtNum(f.results)} results`]
      if (f.samples_shown < f.samples_total) bits.push(`showing newest ${fmtNum(f.samples_shown)}`)
      if (f.criteria_state === 'varies') bits.push('criteria vary by definition')
      if (f.criteria_state === 'none') bits.push('no pass/fail criteria')
      const { row, viz, num } = this.stripRow(f.name, bits.join(' · '))

      const mean = avgs.length ? avgs.reduce((a, b) => a + b, 0) / avgs.length : null
      const meanEl = document.createElement('div')
      meanEl.className = 'm-fmean'
      meanEl.textContent = mean === null ? '—' : fmtNum(mean, dp)
      num.appendChild(meanEl)
      const passEl = document.createElement('div')
      const fails = points.filter(p => p.fail).length
      if (judged && points.length) {
        passEl.className = `m-fpass ${fails ? 'bad' : 'good'}`
        passEl.textContent = fails
          ? `${Math.round((100 * (points.length - fails)) / points.length)}% pass · ${fails} fail`
          : '100% pass'
      } else {
        passEl.className = 'm-fpass na'
        passEl.textContent = judged ? '—' : 'not judged'
      }
      num.appendChild(passEl)

      wrap.appendChild(row)
      fieldStrip(viz, points, {
        criteria: f.criteria,
        judged,
        dp,
        onOpen: (p) => { window.location.href = `/jobs/${p.job_id}/tasks` }
      })
    })
    this.loadValues()
  }

  // One clickable measurement row: name + sub line, a viz slot, a number slot.
  stripRow(name, sub) {
    const row = document.createElement('div')
    row.className = `m-frow${name === this.field ? ' sel' : ''}`
    row.dataset.field = name
    const meta = document.createElement('div')
    meta.className = 'm-fmeta'
    const nameEl = document.createElement('div')
    nameEl.className = 'm-fname'
    nameEl.textContent = name
    nameEl.title = name
    meta.appendChild(nameEl)
    const subEl = document.createElement('div')
    subEl.className = 'm-fsub'
    subEl.textContent = sub
    subEl.title = sub
    meta.appendChild(subEl)
    row.appendChild(meta)
    const viz = document.createElement('div')
    viz.className = 'm-fviz'
    row.appendChild(viz)
    const num = document.createElement('div')
    num.className = 'm-fnum'
    row.appendChild(num)
    row.addEventListener('click', () => this.selectField(name))
    return { row, viz, num }
  }

  // Compare mode strips: one row per measurement any group recorded, every
  // group's samples in its own lane on a shared axis. Criteria zones only
  // when every group that has the measurement agrees on one criteria set.
  renderCompareStrips(results) {
    const host = this.stripsTarget
    host.innerHTML = ''
    const groups = this.groups.map((group, i) => ({
      group,
      byName: Object.fromEntries(results[i].fields.map(f => [f.name, f])),
      color: SLOTS[group.slot],
      label: this.groupLabel(group)
    }))
    const fieldNames = []
    const totals = {}
    results.forEach((data) => {
      data.fields.forEach((f) => {
        if (!(f.name in totals)) { fieldNames.push(f.name); totals[f.name] = 0 }
        totals[f.name] += f.samples_total
      })
    })
    fieldNames.sort((a, b) => totals[b] - totals[a])
    this.stripFields = fieldNames
    if (!fieldNames.length) {
      this.setStripsSub('no numeric result measurements in range for these groups')
      this.renderCompareEmpty('No numeric result measurements to compare')
      return
    }
    this.resolveField(fieldNames)
    this.setStripsSub('one lane per group, one dot = one sample\'s average across jobs · red outline = has a failed result · click a measurement to compare its trend')

    const wrap = document.createElement('div')
    wrap.className = 'm-fstrips'
    host.appendChild(wrap)
    fieldNames.forEach((name) => {
      const present = groups.filter(g => g.byName[name])
      const fields = present.map(g => g.byName[name])
      const criteria = this.agreedCriteria(fields)
      const anyJudged = fields.some(f => f.criteria_state !== 'none')
      const samples = fields.reduce((a, f) => a + f.samples_total, 0)
      const resultsN = fields.reduce((a, f) => a + f.results, 0)
      const bits = [`${fmtNum(samples)} samples`, `${fmtNum(resultsN)} results`]
      if (!criteria && anyJudged) bits.push('criteria differ between groups')
      if (!anyJudged) bits.push('no pass/fail criteria')
      const { row, viz, num } = this.stripRow(name, bits.join(' · '))

      const lanes = groups.map((g) => {
        const f = g.byName[name]
        if (!f) return { label: g.label, color: g.color, entries: [] }
        const outliers = this.outlierIds(f.points)
        return { label: g.label, color: g.color, entries: f.points.map(p => ({ ...p, outlier: outliers.has(p.sample_id) })) }
      })
      const allAvgs = lanes.flatMap(l => l.entries.map(e => e.avg))
      const dp = allAvgs.some(v => !Number.isInteger(v)) ? 1 : 0
      lanes.forEach((lane) => {
        const line = document.createElement('div')
        line.className = 'm-fgmean'
        const dot = document.createElement('span')
        dot.className = 'm-kdot'
        dot.style.background = lane.color
        line.appendChild(dot)
        const avgs = lane.entries.map(e => e.avg)
        const fails = lane.entries.filter(e => e.fail).length
        line.appendChild(document.createTextNode(avgs.length ? fmtNum(avgs.reduce((a, b) => a + b, 0) / avgs.length, dp) : '—'))
        if (fails) {
          const fe = document.createElement('span')
          fe.className = 'm-fgfail'
          fe.textContent = `${fails} fail`
          line.appendChild(fe)
        }
        line.title = `${lane.label}: mean of ${fmtNum(avgs.length)} sample averages`
        num.appendChild(line)
      })

      wrap.appendChild(row)
      groupFieldStrip(viz, lanes, {
        criteria,
        dp,
        onOpen: (p) => { window.location.href = `/jobs/${p.job_id}/tasks` }
      })
    })
    this.loadValues()
  }

  // One {min, max} when every group's definitions agree, else null.
  agreedCriteria(fields) {
    if (!fields.length || fields.some(f => f.criteria_state !== 'one' || !f.criteria)) return null
    const [first] = fields
    const same = fields.every(f => f.criteria.min === first.criteria.min && f.criteria.max === first.criteria.max)
    return same ? first.criteria : null
  }

  // Outlier sample ids, judged WITHIN each material (mirrors the Results
  // Summary card's per-material stats): across mixed materials the minority
  // material's whole cluster would read as "outliers" of the majority one.
  // MAD of 0 (discrete rating scales) disables flagging entirely.
  outlierIds(points) {
    const median = (arr) => {
      const s = [...arr].sort((a, b) => a - b)
      const m = Math.floor(s.length / 2)
      return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2
    }
    const byMaterial = {}
    points.forEach((p) => {
      const key = p.material || ''
      if (!byMaterial[key]) byMaterial[key] = []
      byMaterial[key].push(p)
    })
    const flagged = new Set()
    Object.values(byMaterial).forEach((group) => {
      if (group.length < 4) return
      const vals = group.map(p => p.avg)
      const med = median(vals)
      const mad = median(vals.map(v => Math.abs(v - med)))
      if (!mad) return
      group.forEach((p) => {
        if ((0.6745 * Math.abs(p.avg - med)) / mad >= 3.5) flagged.add(p.sample_id)
      })
    })
    return flagged
  }

  selectField(name) {
    if (this.field === name) return
    this.field = name
    this.stripsTarget.querySelectorAll('.m-frow').forEach((row) => {
      row.classList.toggle('sel', row.dataset.field === name)
    })
    this.loadSeq = (this.loadSeq || 0) + 1
    this.loadValues()
  }

  loadValues() {
    if (!this.field) {
      if (this.compareActive()) this.renderCompareEmpty('Click a measurement above to compare its trend')
      else this.renderEmpty('Click a measurement above to open its trend')
      return
    }
    if (this.compareActive()) {
      this.loadCompareValues()
      return
    }
    const p = this.baseParams()
    p.set('field', this.field)
    const qs = p.toString()
    this.fetchJson(`${this.data.get('valuesUrl')}?${qs}`, data => this.render(data))
    this.fetchJson(`${this.data.get('fieldJobsUrl')}?${qs}`, data => this.renderJobs(data))
    this.fetchJson(`${this.data.get('fieldCriteriaUrl')}?${qs}`, data => this.renderCriteria(data))
  }

  // Weekly medians up to 180 days, monthly past that: the same rule as the
  // Jobs tab's status-to-status card (JobsQueries::MONTHLY_BUCKETS_AFTER).
  compareBucket() {
    const range = this.rangeTarget.value
    const now = new Date()
    const days = range === 'ytd'
      ? (now - new Date(now.getFullYear(), 0, 1)) / 86400000
      : Number(range)
    return days > 180 ? 'month' : 'week'
  }

  loadCompareValues() {
    const bucket = this.compareBucket()
    const { field } = this
    this.fetchPerGroup((group) => {
      const p = this.baseParams(group)
      p.set('field', field)
      p.set('bucket', bucket)
      return `${this.data.get('valuesUrl')}?${p.toString()}`
    }).then(results => this.renderCompareValues(results, bucket))
  }

  renderCompareEmpty(msg) {
    this.cmpTilesTarget.innerHTML = ''
    this.cmpLegendTarget.innerHTML = ''
    this.cmpChartTarget.innerHTML = ''
    this.cmpTitleTarget.textContent = 'Value over time'
    this.cmpSubTarget.textContent = msg
  }

  // Per-group KPI tiles (median, values, fails) and one median line per
  // group. No control limits or criteria lines here: the groups' criteria
  // can differ, and the strips above already show the zones when they agree.
  renderCompareValues(results, bucket) {
    const groups = this.groups.map((group, i) => ({
      group, data: results[i], color: SLOTS[group.slot], label: this.groupLabel(group)
    }))
    const meds = groups.flatMap(g => g.data.buckets.map(b => b.med)).concat(groups.map(g => g.data.summary.median))
    const dp = meds.some(v => v !== null && !Number.isInteger(v)) ? 1 : 0

    const tiles = this.cmpTilesTarget
    tiles.innerHTML = ''
    groups.forEach((g) => {
      const s = g.data.summary
      const tile = document.createElement('div')
      tile.className = 'm-card m-cmp-tile'
      kpiTile(tile, {
        label: `${GROUP_NAMES[g.group.slot]} · ${g.label}`.slice(0, 80),
        dot: g.color,
        value: s.n && s.median !== null ? fmtNum(s.median, dp) : '—',
        delta: null,
        deltaText: null,
        sub: `median ${this.field} · ${fmtNum(s.n)} value${s.n === 1 ? '' : 's'} · ${fmtNum(s.fails)} failed`
      })
      tiles.appendChild(tile)
    })

    const legend = this.cmpLegendTarget
    legend.innerHTML = ''
    groups.forEach((g) => {
      const key = document.createElement('span')
      key.className = 'm-key'
      const sw = document.createElement('span')
      sw.className = 'm-swatch'
      sw.style.background = g.color
      key.appendChild(sw)
      key.appendChild(document.createTextNode(`${GROUP_NAMES[g.group.slot]}: ${g.label}`.slice(0, 70)))
      legend.appendChild(key)
    })

    const period = bucket === 'month' ? 'Monthly' : 'Weekly'
    this.cmpTitleTarget.textContent = `${this.field} over time`
    this.cmpSubTarget.textContent = `${period} median per group · ${bucket}s without results are skipped · hover for counts`
    groupTrend(this.cmpChartTarget, {
      series: groups.map(g => ({
        label: g.label,
        color: g.color,
        points: g.data.buckets.map(b => ({ at: b.start, value: b.med, n: b.n, fails: b.fails }))
      }))
    }, { dp, period: bucket })
  }

  renderEmpty(msg = 'Pick a task above') {
    if (this.compareActive()) this.renderCompareEmpty(msg)
    this.chartSubTarget.textContent = msg
    this.chartTarget.innerHTML = ''
    this.histTarget.innerHTML = ''
    this.failedTarget.innerHTML = ''
    this.critTarget.innerHTML = ''
    this.critSubTarget.textContent = ''
    this.jobsTarget.innerHTML = ''
    this.jobsSubTarget.textContent = ''
    const blank = { label: '', value: '—', delta: null, deltaText: null }
    kpiTile(this.kpiNTarget, { ...blank, label: 'Results' })
    kpiTile(this.kpiMeanTarget, { ...blank, label: 'Mean' })
    kpiTile(this.kpiSdTarget, { ...blank, label: 'Std dev' })
    kpiTile(this.kpiPassTarget, { ...blank, label: 'Pass rate' })
  }

  // Drill-in idea #2: the criteria sets judging this field's values, with
  // counts, so "criteria vary" becomes inspectable instead of a shrug.
  renderCriteria(data) {
    const sets = data.criteria_sets
    this.critTarget.innerHTML = ''
    if (!sets.length) {
      this.critSubTarget.textContent = 'This measurement records values without judging them'
      const empty = document.createElement('div')
      empty.className = 'm-empty'
      empty.textContent = 'No pass/fail criteria defined on this measurement.'
      this.critTarget.appendChild(empty)
      return
    }
    const anyOverrides = sets.some(s => s.has_overrides)
    this.critSubTarget.textContent = (sets.length === 1
      ? 'All matched definitions agree, so zones show on the strip above'
      : `${sets.length} disagreeing criteria sets · zones appear when definitions agree`)
      + (anyOverrides ? ' · * carries per-material overrides' : '')
    renderTable(
      this.critTarget,
      [{ label: 'Criteria' }, { label: 'Results judged', num: true }, { label: 'Fails', num: true }],
      sets.map(s => [this.criteriaLabel(s) + (s.has_overrides ? ' *' : ''), fmtNum(s.judged), s.fails ? fmtNum(s.fails) : '—'])
    )
  }

  criteriaLabel(s) {
    const f = v => fmtNum(v, Number.isInteger(v) ? 0 : 1)
    const hasMin = s.min !== null && s.min !== undefined
    const hasMax = s.max !== null && s.max !== undefined
    if (hasMin && hasMax) return `${f(s.min)} – ${f(s.max)}`
    if (hasMin) return `≥ ${f(s.min)}`
    if (hasMax) return `≤ ${f(s.max)}`
    return '—'
  }

  // Drill-in idea #1: per-job rollup bridging to each job's tasks screen,
  // where the Results Summary card owns the within-job story.
  renderJobs(data) {
    this.jobsTarget.innerHTML = ''
    if (!data.jobs.length) {
      this.jobsSubTarget.textContent = ''
      const empty = document.createElement('div')
      empty.className = 'm-empty'
      empty.textContent = 'No jobs recorded this measurement in range.'
      this.jobsTarget.appendChild(empty)
      return
    }
    const dp = data.jobs.some(j => !Number.isInteger(j.mean)) ? 1 : 0
    const trimNote = data.total > data.jobs.length ? ` · showing ${data.jobs.length} most recent` : ''
    this.jobsSubTarget.textContent = `${fmtNum(data.total)} job${data.total === 1 ? '' : 's'} in range${trimNote} · click a job to open its tasks screen`
    renderTable(
      this.jobsTarget,
      [{ label: 'ID' }, { label: 'Job' }, { label: 'Latest' }, { label: 'Samples', num: true }, { label: 'Results', num: true }, { label: 'Mean', num: true }, { label: 'Fails', num: true }],
      data.jobs.map(j => [
        `#${j.display_id}`, j.job, new Date(j.at).toLocaleDateString(), fmtNum(j.samples), fmtNum(j.results),
        fmtNum(j.mean, dp), j.fails ? fmtNum(j.fails) : '—'
      ]),
      (ri) => { window.location.href = `/jobs/${data.jobs[ri].job_id}/tasks` }
    )
  }

  render(data) {
    if (data.mode === 'weekly') {
      this.renderWeeklyMode(data)
      return
    }
    const pts = data.points
    const vals = pts.map(p => p.value)
    const n = vals.length
    const mean = n ? vals.reduce((a, b) => a + b, 0) / n : null
    const sd = n > 1 ? Math.sqrt(vals.reduce((a, b) => a + ((b - mean) ** 2), 0) / (n - 1)) : null
    const sorted = [...vals].sort((a, b) => a - b)
    const median = n ? sorted[Math.floor((n - 1) / 2)] : null
    const passes = pts.filter(p => p.status === 'pass').length
    const fails = pts.filter(p => p.status === 'fail').length
    const tested = passes + fails
    const passPct = tested ? Math.round((100 * passes) / tested) : null
    const dp = vals.some(v => !Number.isInteger(v)) ? 1 : 0

    kpiTile(this.kpiNTarget, {
      label: 'Results',
      value: fmtNum(n),
      delta: null,
      deltaText: null,
      sub: data.truncated ? `showing newest ${fmtNum(pts.length)} (capped)` : null
    })
    kpiTile(this.kpiMeanTarget, {
      label: 'Mean',
      value: mean === null ? '—' : fmtNum(mean, dp),
      delta: null,
      deltaText: null,
      sub: median === null ? null : `median ${fmtNum(median, dp)}`
    })
    kpiTile(this.kpiSdTarget, { label: 'Std dev', value: sd === null ? '—' : fmtNum(sd, 1), delta: null, deltaText: null })
    kpiTile(this.kpiPassTarget, {
      label: 'Pass rate',
      value: passPct === null ? '—' : `${passPct}%`,
      delta: null,
      deltaText: null,
      sub: tested ? `of ${fmtNum(tested)} judged values` : 'no pass/fail criteria matched'
    })

    const refLines = []
    if (data.criteria) {
      if (data.criteria.max !== null && data.criteria.max !== undefined) refLines.push({ y: data.criteria.max, label: `Max ${fmtNum(data.criteria.max, dp)}`, kind: 'criteria' })
      if (data.criteria.min !== null && data.criteria.min !== undefined) refLines.push({ y: data.criteria.min, label: `Min ${fmtNum(data.criteria.min, dp)}`, kind: 'criteria' })
    }
    if (mean !== null) refLines.push({ y: mean, label: `x̄ ${fmtNum(mean, dp)}`, kind: 'mean' })

    this.chartSubTarget.textContent = `${this.field} · ${this.taskSummary()}${
      data.criteria ? '' : ' · no single criteria set, so points are judged by their stored status'
    }${data.truncated ? ' · truncated to newest 2,000 points' : ''}`
    controlChart(this.chartTarget, pts, { refLines, dp })

    renderTable(
      this.chartTableTarget,
      [{ label: 'Date' }, { label: 'Job' }, { label: 'Value', num: true }, { label: 'Status' }],
      pts.slice(-200).reverse().map(p => [new Date(p.at).toLocaleDateString(), p.job, fmtNum(p.value, dp), p.status]),
    )

    this.histSubTarget.textContent = `${fmtNum(n)} values${data.criteria ? ' · dashed lines mark the pass/fail criteria' : ''}`
    histogram(this.histTarget, vals, { min: data.criteria && data.criteria.min, max: data.criteria && data.criteria.max, dp })

    const failedRows = pts.filter(p => p.status === 'fail').slice(-8).reverse()
    if (failedRows.length) {
      renderTable(
        this.failedTarget,
        [{ label: 'Date' }, { label: 'Job' }, { label: 'Value', num: true }],
        failedRows.map(p => [new Date(p.at).toLocaleDateString(), p.job, fmtNum(p.value, dp)]),
      )
    } else {
      this.failedTarget.innerHTML = ''
      const empty = document.createElement('div')
      empty.className = 'm-empty'
      empty.textContent = tested ? 'No failed values in range. Nice.' : 'No pass/fail criteria on this field.'
      this.failedTarget.appendChild(empty)
    }
  }

  // Drill-in idea #3: above the volume threshold the server returns weekly
  // median/quartile stats instead of points (no more truncation), and the
  // histogram arrives as server-side bins.
  renderWeeklyMode(data) {
    const s = data.summary
    const dp = data.weeks.some(w => !Number.isInteger(w.med)) ? 1 : 0

    kpiTile(this.kpiNTarget, {
      label: 'Results',
      value: fmtNum(s.n),
      delta: null,
      deltaText: null,
      sub: 'high volume, aggregated weekly'
    })
    kpiTile(this.kpiMeanTarget, {
      label: 'Mean',
      value: s.mean === null ? '—' : fmtNum(s.mean, dp),
      delta: null,
      deltaText: null,
      sub: s.median === null ? null : `median ${fmtNum(s.median, dp)}`
    })
    kpiTile(this.kpiSdTarget, { label: 'Std dev', value: s.sd === null ? '—' : fmtNum(s.sd, 1), delta: null, deltaText: null })
    const tested = s.passes + s.fails
    kpiTile(this.kpiPassTarget, {
      label: 'Pass rate',
      value: tested ? `${Math.round((100 * s.passes) / tested)}%` : '—',
      delta: null,
      deltaText: null,
      sub: tested ? `of ${fmtNum(tested)} judged values` : 'no pass/fail criteria matched'
    })

    const refLines = []
    if (data.criteria) {
      if (data.criteria.max !== null && data.criteria.max !== undefined) refLines.push({ y: data.criteria.max, label: `Max ${fmtNum(data.criteria.max, dp)}`, kind: 'criteria' })
      if (data.criteria.min !== null && data.criteria.min !== undefined) refLines.push({ y: data.criteria.min, label: `Min ${fmtNum(data.criteria.min, dp)}`, kind: 'criteria' })
    }
    if (s.mean !== null) refLines.push({ y: s.mean, label: `x̄ ${fmtNum(s.mean, dp)}`, kind: 'mean' })

    this.chartSubTarget.textContent = `${this.field} · ${this.taskSummary()} · ${fmtNum(s.n)} results: weekly median + middle-50% whiskers, red dot = week with fails${
      data.criteria ? '' : ' · no single criteria set, so values are judged by their stored status'
    }`
    weeklyWhiskers(this.chartTarget, data.weeks, { refLines, dp })

    renderTable(
      this.chartTableTarget,
      [{ label: 'Week' }, { label: 'Results', num: true }, { label: 'Median', num: true }, { label: 'Q1', num: true }, { label: 'Q3', num: true }, { label: 'Min', num: true }, { label: 'Max', num: true }, { label: 'Fails', num: true }],
      data.weeks.map(w => [
        `Week of ${new Date(w.week).toLocaleDateString()}`, fmtNum(w.n), fmtNum(w.med, dp),
        fmtNum(w.q1, dp), fmtNum(w.q3, dp), fmtNum(w.min, dp), fmtNum(w.max, dp),
        w.fails ? fmtNum(w.fails) : '—'
      ]),
    )

    this.histSubTarget.textContent = `${fmtNum(s.n)} values, binned server-side${data.criteria ? ' · dashed lines mark the pass/fail criteria' : ''}`
    binsHistogram(this.histTarget, data.bins, { min: data.criteria && data.criteria.min, max: data.criteria && data.criteria.max, dp })

    if (data.recent_fails.length) {
      renderTable(
        this.failedTarget,
        [{ label: 'Date' }, { label: 'Job' }, { label: 'Value', num: true }],
        data.recent_fails.map(p => [new Date(p.at).toLocaleDateString(), p.job, fmtNum(p.value, dp)]),
      )
    } else {
      this.failedTarget.innerHTML = ''
      const empty = document.createElement('div')
      empty.className = 'm-empty'
      empty.textContent = tested ? 'No failed values in range. Nice.' : 'No pass/fail criteria on this field.'
      this.failedTarget.appendChild(empty)
    }
  }

  toggleTable(ev) {
    const btn = ev.currentTarget
    const card = btn.closest('.m-card')
    const showing = card.classList.toggle('show-table')
    btn.textContent = showing ? 'Chart' : 'Table'
  }
}
